import type {
  AnnotationMediaResolver,
  AnnotationMediaSource,
  AnnotationStream,
  AnnotationWorkspaceSnapshot,
  AnnotationWorkspaceSource,
} from "@carbon6/c6-frontend-design/annotation";
import { AnnotationIdentityRegistry } from "../model/annotationIdentity";
import { createAnnotationTargets } from "../model/annotationTargets";

function mediaKey(sequenceId: string, streamId: string): string {
  return JSON.stringify([sequenceId, streamId]);
}

export class RegistryAnnotationWorkspaceSource implements AnnotationWorkspaceSource {
  private readonly listeners = new Set<() => void>();
  private readonly media = new Map<string, AnnotationMediaSource>();
  private snapshot: AnnotationWorkspaceSnapshot;

  constructor(
    private readonly identities: AnnotationIdentityRegistry,
    collectionLabel: string,
    cameras: readonly string[] = [],
  ) {
    this.snapshot = {
      collectionId: identities.collectionId,
      collectionLabel,
      revision: 1,
      sequences: [],
      streams: this.toStreams(cameras),
      targets: createAnnotationTargets(identities),
      annotationSets: [],
    };
  }

  getSnapshot = (): AnnotationWorkspaceSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  resolveMediaSource: AnnotationMediaResolver = ({ sequence, stream }) =>
    this.media.get(mediaKey(sequence.id, stream.id)) ?? null;

  publishCameras(cameras: readonly string[]): void {
    const streams = this.toStreams(cameras);
    if (
      streams.length === this.snapshot.streams.length &&
      streams.every((stream, index) => stream.id === this.snapshot.streams[index]?.id)
    ) {
      return;
    }
    this.publish({ ...this.snapshot, streams });
  }

  publishCollectionLabel(collectionLabel: string): void {
    if (collectionLabel === this.snapshot.collectionLabel) return;
    this.publish({ ...this.snapshot, collectionLabel });
  }

  publishMedia(sequenceId: string, streamId: string, source: AnnotationMediaSource): void {
    const key = mediaKey(sequenceId, streamId);
    const current = this.media.get(key);
    if (
      current?.src === source.src &&
      current.poster === source.poster &&
      current.durationSeconds === source.durationSeconds &&
      current.aspectRatio === source.aspectRatio &&
      current.crossOrigin === source.crossOrigin
    ) {
      return;
    }
    this.media.set(key, source);
    this.publish(this.snapshot);
  }

  clearMedia(): void {
    if (this.media.size === 0) return;
    this.media.clear();
    this.publish(this.snapshot);
  }

  private toStreams(cameras: readonly string[]): AnnotationStream[] {
    return [...new Set(cameras)].map((camera) => ({
      id: this.identities.streamId(camera),
      label: camera,
    }));
  }

  private publish(next: AnnotationWorkspaceSnapshot): void {
    this.snapshot = { ...next, revision: this.snapshot.revision + 1 };
    for (const listener of this.listeners) listener();
  }
}
